import { ModelImage } from '@acme/db';
import { transformCloudflareImageUrl } from '@acme/lib';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import Image from 'next/image';
import React from 'react';
import { PhotoPackName, PhotoPackTag, PhotoPackGrid } from '.';
import { Skeleton } from '@components/ui/skeleton';
import { Card, CardContent, CardFooter } from '@components/ui/card';

interface HeroProps {
  photoPackCover?: string | null;
  className?: string;
  enableSampleGallery?: boolean;
  checked?: boolean;
  style?: React.CSSProperties;
  onCustomize?: (checked: boolean) => void;
  relationship?: 'couple' | 'friends';
  useMeshGradient?: boolean;
  singleColor?: string;
  isSheetOpen?: boolean;
  setIsSheetOpen?: (open: boolean) => void;
  limitSamples?: number;
  showSelfies?: boolean;
  skeleton?: boolean;
  disableLink?: boolean;
  photoPack?: {
    name: string;
    id: string;
    tag: {
      name: string;
      url: string;
    };
    url: string;
    sampleImages?: (Pick<ModelImage, 'id' | 'imageUrl'> & { selfieImageUrl?: string | null })[];
  };
}

export function Hero({
  className,
  checked,
  onCustomize,
  setIsSheetOpen,
  limitSamples = 4,
  disableLink = false,
  photoPack,
  skeleton = false
}: HeroProps) {
  if (skeleton) {
    return (
      <div className="aspect-[2/1] grid grid-cols-2 bg-foreground/10 animate-pulse rounded-lg overflow-hidden">
        <div className="w-full bg-foreground/10"></div>
        <div className="w-full bg-foreground/5 flex items-center justify-center p-10">
          <Card className="w-full bg-transparent border-none shadow-none">
            <CardContent className="p-4 pb-0">
              <div className="grid grid-cols-2 gap-2">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="aspect-[4/3] w-full h-auto animate-none bg-foreground/10" />
                ))}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-start px-4 py-2">
              <Skeleton className="h-3 w-3/4 mb-2 animate-none bg-foreground/10" />
              <Skeleton className="h-3 w-1/3 mb-1 animate-none bg-foreground/10" />
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Get sample images to display
  const displayImages =
    photoPack?.sampleImages?.length! > 1 ? photoPack?.sampleImages?.slice(1, limitSamples + 1)! : []!;

  // Get the main image URL and transform it to use the orig2 variant
  const originalMainImage = photoPack?.sampleImages?.[0]?.imageUrl;
  const mainImage = originalMainImage
    ? transformCloudflareImageUrl(originalMainImage, { variant: 'orig2' })
    : undefined;
  return (
    <div className={cn('w-full relative overflow-hidden rounded-xl text-white dark bg-black', className)}>
      <div className="flex flex-col md:flex-row">
        {/* Left side - Main image */}
        <div className="md:w-1/2 relative">
          <div className="relative w-full h-auto aspect-square overflow-hidden">
            {mainImage ? (
              <Image
                src={mainImage}
                alt={photoPack?.name!}
                fill
                className="object-cover"
                style={{ objectPosition: '50% 40%' }}
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground">No image available</span>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Combined blur image and theme info */}
        <div className="md:w-1/2 relative">
          {/* Blurred background image */}
          <div className="absolute inset-0 w-full h-full   overflow-hidden">
            {mainImage ? (
              <Image
                src={mainImage}
                alt={photoPack?.name!}
                fill
                className="object-cover blur-3xl scale-150 opacity-60 -rotate-180"
                style={{ objectPosition: '50% -20%' }}
              />
            ) : (
              <div className="w-full h-full bg-muted"></div>
            )}
          </div>

          {/* Theme info overlay */}
          <div className="relative z-[1] p-6 md:px-10 lg:px-20 h-full flex flex-col justify-center gap-6 md:gap-6">
            {/* Sample images grid */}
            {displayImages?.length > 0 && (
              <PhotoPackGrid
                displayImages={displayImages}
                disableLink={disableLink}
                setIsSheetOpen={setIsSheetOpen}
                photoPack={photoPack}
                forceTallAspectRatio={true}
                className="grid grid-cols-2 gap-2 -mt-20 md:mt-0 z-[2]"
              />
            )}
            <div className="flex flex-row md:flex-col justify-between items-center md:items-start gap-2 md:gap-6">
              <div className="flex flex-col min-w-0 relative group">
                <div className="self-stretch text-xs text-muted-foreground uppercase tracking-wider truncate">
                  <PhotoPackTag photoPack={photoPack!} disableLink={disableLink} />
                </div>

                <h2 className="text-3xl md:text-5xl font-bold mb-0 truncate break-all md:break-words text-wrap line-clamp-1 md:line-clamp-2">
                  <PhotoPackName photoPack={photoPack!} disableLink={disableLink} />
                </h2>
              </div>
              <Button
                variant="default"
                className="md:px-8 w-auto md:w-full px-6 py-2 text-accent font-semibold rounded-full"
                onClick={() => onCustomize?.(!checked)}
              >
                {checked ? 'Edit' : 'Get'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
